import log from 'loglevel';

import axios from 'axios';

import { baseURL, csrfToken, saveUrl, generateUrl } from './config.js';

const axiosConfig = {
  baseURL,
};

const httpClient = axios.create(axiosConfig);

httpClient.interceptors.request.use(function (config) {
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
});

/**
 * Turn a list of files into a tree structure
 *
 * @param {object} - output data in path->filedata format
 *
 * Takes a flat object and turns it into a tree
 * structure where a directory is represented by an
 * object and leaves are the file contents as strings
 */
function prepareOutputTree(data) {
  /**
   * Create an entry for file in the tree structure
   *
   * This also creates any intermediate directory nodes
   * if they're not already created. In effect, this
   * does the following:
   *
   *     makedirs(dirname(path))
   *     write(path, data)
   */
  function createFileNode(root, path, fileData) {
    const parts = path.split('/');
    const nodeNames = parts.slice(0, parts.length - 1);
    let node = root;
    for (const name of nodeNames) {
      if (!node[name]) node[name] = {};
      node = node[name];
    }
    node[parts[parts.length - 1]] = fileData;
  }

  const tree = {};
  for (const path in data) {
    createFileNode(tree, path, data[path]);
  }
  return tree;
}

/**
 * Upload recipe to the server (async)
 *
 * @param {object} recipe - Recipe object to upload
 *
 * On success, the server will return an object with
 * generated code in path->contents, which we parse
 * with `prepareOutputTree` before returning to caller.
 *
 * If there's an error with the recipe, we'll throw
 * a textual (formatted) error response. If there's
 * a system or network error, we just throw that error.
 */
export const uploadRecipe = async (recipe) => {
  if (!saveUrl) {
    log.warn('uploadRecipe(): no saveUrl specified, not uploading; recipe = ', recipe);
    return {};
  }

  try {
    log.debug('uploadRecipe(): uploading recipe');
    const response = await httpClient.post(saveUrl, recipe);
    return prepareOutputTree(response.data.output);
  } catch (err) {
    if (err.response) {
      const msg = err.response.data.text || err.response.data;
      log.info(`uploadRecipe(): server returned error: ${msg}`);
      throw msg;
    } else if (err.request) {
      log.error(`uploadRecipe(): HTTP request error: ${err.request}`);
      throw "Server error, please try again later";
    } else {
      log.error(`uploadRecipe(): unknown error: ${err}`);
      throw "Unknown error";
    }
  }
};


export const generateProject = async (description) => {
  if (!generateUrl) {
    log.warn('generateProject(): no generateUrl specified, not uplading');
    return null;
  }

  try {
    const response = await httpClient.post(generateUrl, { description });
    return response.data;
  } catch (err) {
    if (err.response) {
      const msg = err.response.data.text || err.response.data;
      log.info(`generateProject(): server returned error: ${msg}`);
      throw msg;
    } else if (err.request) {
      log.error(`generateProject(): HTTP request error: ${err.request}`);
      throw "Server error, please try again later";
    } else {
      log.error(`generateProject(): unknown error: ${err}`);
      throw "Unknown error";
    }
  }

}
